import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bookmark, Calculator, LogOut, Menu, Route, UserRound, Users, X } from 'lucide-react';
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from './ui/sheet';
import Tooltip from './ui/Tooltip';
import { Logo } from './Logo';
import { SOCIAL_LINKS } from './social';
import { useAuth } from '../features/auth/context';
import { safeNextPath } from '../features/auth/nextPath';
import { analytics } from '../shared/analytics';
import { cn } from '../lib/utils';

// A port of the homepage header (jody-landing-page, src/components/Header.tsx;
// copy rules in section 4.1 of the Home V1 copy deck): same classes, icons,
// Sheet and "CTA Click" events, so a change there carries over here. What the
// app does differently:
// - logged in, "Log in" becomes "Hello {first name}" followed by "Log out";
// - the logo and Why Jody go to the landing site, the rest are app routes;
// - My Trips carries the active underline anywhere in the trips area;
// - the mobile menu also lists Friends (logged in) and the Travel Impact
//   Calculator, which are not in the bar;
// - "Free. No account needed." shows to logged-out visitors only, as the app's
//   Tooltip rather than a native title (docs/codebase/ui-conventions.md).

const LANDING_PAGE_URL = process.env.REACT_APP_LANDING_PAGE_URL || 'http://localhost:5173';
const PLAN_TRIP_PATH = '/trips/new';
const MY_TRIPS_PATH = '/trips';
const PLAN_TRIP_HINT = 'Free. No account needed.';

/** Height of the sticky header, for inline sticky offsets below it
 *  (JourneyDetailPage). Same value as --header-height in index.css. */
export const APP_HEADER_HEIGHT = 72;

type NavItem = {
  label: string;
  href: string;
  external?: boolean;
};

// No "Home" item: the logo goes home.
const NAV_ITEMS: NavItem[] = [
  { label: 'Why Jody', href: `${LANDING_PAGE_URL}/#why-jody`, external: true },
  { label: 'Night trains', href: '/night-trains' },
];

const navLinkClass = (active: boolean) =>
  cn(
    'flex h-full items-center border-b-2 pt-0.5 text-[15px] transition-colors hover:text-jody-forest',
    active ? 'border-jody-green font-bold text-jody-forest' : 'border-transparent font-semibold text-jody-neutral-600',
  );

const utilityLinkClass =
  'flex items-center gap-1.5 text-[15px] font-semibold text-jody-neutral-600 transition-colors hover:text-jody-forest';

// Not a link: one weight lighter so it does not read as tappable.
const greetingClass = 'flex cursor-default items-center gap-1.5 text-[15px] font-medium text-jody-neutral-600';

// hover:text-white (not on the homepage): the app's global a:hover color in
// index.css would otherwise turn the label green.
const planTripClass =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border-[1.5px] border-transparent bg-jody-green font-bold leading-none tracking-[-0.005em] text-white transition-colors hover:bg-jody-green-600 hover:text-white active:bg-jody-green-700';

// Same event and cta_name values as the homepage header; the event's source
// property ('app' here, 'landing-page' there) tells the two apart.
// Destinations are absolute URLs, as the homepage reports them.
const appUrl = (path: string) => `${window.location.origin}${path}`;
const trackCta = (name: string, destination: string) => () => analytics.trackCTAClick(name, destination);

const Header: React.FC = () => {
  const { user, logout, loading } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const [menuOpen, setMenuOpen] = useState(false);

  // The header stays mounted across routes, so any navigation (including the
  // back button) closes the menu.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname, location.search]);

  // The "Log in" link carries the visitor's place as ?next= so login lands
  // them back where they were, essential for guests deep in a trip (anonymous
  // access v1). On the auth pages themselves the existing ?next= is passed
  // through instead (the auth page is never a destination).
  const authNext = (() => {
    const existing = safeNextPath(new URLSearchParams(location.search).get('next'));
    if (existing) return existing;
    return safeNextPath(location.pathname + location.search);
  })();
  const loginHref = authNext ? `/login?next=${encodeURIComponent(authNext)}` : '/login';

  const handleLogout = async () => {
    try {
      await logout();
      // Redirect to landing page after logout
      window.location.href = LANDING_PAGE_URL;
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActive = (item: NavItem) => !item.external && item.href === pathname;
  // My Trips stays underlined inside a trip or one of its routes; the new-trip
  // form belongs to "Plan a trip" instead.
  const myTripsActive =
    pathname === MY_TRIPS_PATH ||
    (pathname.startsWith(`${MY_TRIPS_PATH}/`) && pathname !== PLAN_TRIP_PATH) ||
    pathname.startsWith('/journeys/');
  // Unknown while auth loads: hidden, so a logged-in user never sees it flash.
  const showPlanTripHint = !loading && !user;

  const renderNavItem = (item: NavItem, className: string) =>
    item.external ? (
      <a key={item.label} href={item.href} className={className}>
        {item.label}
      </a>
    ) : (
      <Link key={item.label} to={item.href} aria-current={isActive(item) ? 'page' : undefined} className={className}>
        {item.label}
      </Link>
    );

  const planTripBar = (
    <Link
      to={PLAN_TRIP_PATH}
      onClick={trackCta('Header Plan a trip', appUrl(PLAN_TRIP_PATH))}
      className={cn(planTripClass, 'h-9 px-3.5 text-sm')}
    >
      <Route className="h-4 w-4" aria-hidden="true" />
      Plan a trip
    </Link>
  );

  // A row of the mobile menu's list; closes the menu (also when the link
  // points at the page already open).
  const menuRow = (to: string, label: string, Icon: typeof Users, active: boolean, onClick?: () => void) => (
    <SheetClose asChild key={to}>
      <Link
        to={to}
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
        className={cn(utilityLinkClass, 'h-12 gap-2.5', active && 'font-bold text-jody-forest')}
      >
        <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
        {label}
      </Link>
    </SheetClose>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-jody-neutral-200 bg-jody-paper/[0.86] font-jody backdrop-blur-md">
      <div className="mx-auto flex h-[var(--header-height)] max-w-[1200px] items-center gap-[clamp(20px,3vw,40px)] px-[clamp(20px,4vw,48px)]">
        <a href={LANDING_PAGE_URL} aria-label="Jody home" className="flex-none">
          <Logo />
        </a>

        {/* Desktop */}
        <nav aria-label="Main" className="hidden h-full flex-1 items-center gap-[clamp(18px,2.4vw,32px)] lg:flex">
          {NAV_ITEMS.map((item) => renderNavItem(item, navLinkClass(isActive(item))))}
          {showPlanTripHint ? (
            <Tooltip content={PLAN_TRIP_HINT} widthClassName="w-max" bare>
              {planTripBar}
            </Tooltip>
          ) : (
            planTripBar
          )}
        </nav>

        <div className="hidden h-full items-center gap-[clamp(16px,2vw,24px)] lg:flex">
          {SOCIAL_LINKS.length > 0 && (
            <>
              <div className="flex items-center gap-3">
                {SOCIAL_LINKS.map(({ name, url, Icon }) => (
                  <Tooltip key={name} content={`Jody on ${name}`} widthClassName="w-max" bare>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Jody on ${name}`}
                      onClick={trackCta(`Header ${name}`, url)}
                      className="-m-1 rounded-full p-1 text-jody-neutral-600 transition-colors hover:text-jody-forest"
                    >
                      <Icon size={18} aria-hidden="true" />
                    </a>
                  </Tooltip>
                ))}
              </div>
              <span aria-hidden="true" className="h-5 w-px bg-jody-neutral-300" />
            </>
          )}
          <Link
            to={MY_TRIPS_PATH}
            aria-current={myTripsActive ? 'page' : undefined}
            onClick={trackCta('Header My trips', appUrl(MY_TRIPS_PATH))}
            className={cn(
              utilityLinkClass,
              'h-full border-b-2 border-transparent pt-0.5',
              myTripsActive && 'border-jody-green font-bold text-jody-forest',
            )}
          >
            <Bookmark className="h-[17px] w-[17px]" aria-hidden="true" />
            My Trips
          </Link>
          <span aria-hidden="true" className="h-5 w-px bg-jody-neutral-300" />
          {loading ? (
            <span className={greetingClass}>Loading…</span>
          ) : user ? (
            <div className="flex items-center gap-[clamp(16px,2vw,24px)]">
              <span className={greetingClass}>
                <UserRound className="h-[18px] w-[18px]" aria-hidden="true" />
                <span className="max-w-[16ch] truncate">Hello {user.firstName}</span>
              </span>
              <button type="button" onClick={handleLogout} className={utilityLinkClass}>
                <LogOut className="h-[17px] w-[17px]" aria-hidden="true" />
                Log out
              </button>
            </div>
          ) : (
            <Link to={loginHref} onClick={trackCta('Header Login', appUrl(loginHref))} className={utilityLinkClass}>
              <UserRound className="h-[18px] w-[18px]" aria-hidden="true" />
              Log in
            </Link>
          )}
        </div>

        {/* Mobile and tablet: Instagram stays visible left of the menu button */}
        <div className="flex-1 lg:hidden" />
        <div className="-mr-3 flex items-center lg:hidden">
          {SOCIAL_LINKS.map(({ name, url, Icon }) => (
            <a
              key={name}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Jody on ${name}`}
              onClick={trackCta(`Header Mobile ${name}`, url)}
              className="flex h-11 w-11 items-center justify-center rounded-full text-jody-forest transition-colors hover:bg-jody-neutral-100 hover:text-jody-forest"
            >
              <Icon size={20} aria-hidden="true" />
            </a>
          ))}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Open menu"
                className="flex h-11 w-11 items-center justify-center rounded-full text-jody-forest transition-colors hover:bg-jody-neutral-100"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              aria-describedby={undefined}
              className="flex w-full flex-col gap-0 border-jody-neutral-200 bg-jody-paper p-0 font-jody sm:max-w-sm [&>button]:hidden"
            >
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex h-[var(--header-height)] flex-none items-center justify-between border-b border-jody-neutral-200 px-5">
                <a href={LANDING_PAGE_URL} aria-label="Jody home">
                  <Logo />
                </a>
                <SheetClose asChild>
                  <button
                    type="button"
                    aria-label="Close menu"
                    className="-mr-3 flex h-11 w-11 items-center justify-center rounded-full text-jody-forest transition-colors hover:bg-jody-neutral-100"
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </SheetClose>
              </div>

              <div className="flex flex-1 flex-col overflow-y-auto px-5 pb-6">
                <nav aria-label="Main" className="flex flex-col py-2">
                  {NAV_ITEMS.map((item) => {
                    const className = cn(
                      'flex h-14 items-center border-b border-jody-neutral-200 text-lg transition-colors hover:text-jody-forest',
                      isActive(item) ? 'font-bold text-jody-forest' : 'font-semibold text-jody-ink',
                    );
                    return item.external ? (
                      renderNavItem(item, className)
                    ) : (
                      <SheetClose asChild key={item.label}>
                        {renderNavItem(item, className)}
                      </SheetClose>
                    );
                  })}
                </nav>

                <SheetClose asChild>
                  <Link
                    to={PLAN_TRIP_PATH}
                    onClick={trackCta('Header Menu Plan a trip', appUrl(PLAN_TRIP_PATH))}
                    className={cn(planTripClass, 'mt-6 h-[52px] px-6 text-base')}
                  >
                    <Route className="h-[18px] w-[18px]" aria-hidden="true" />
                    Plan a trip
                  </Link>
                </SheetClose>
                {showPlanTripHint && (
                  <p className="mt-2 text-center text-sm font-medium text-jody-neutral-600">{PLAN_TRIP_HINT}</p>
                )}

                <div className="mt-4 flex flex-col">
                  {menuRow(
                    MY_TRIPS_PATH,
                    'My Trips',
                    Bookmark,
                    myTripsActive,
                    trackCta('Header Menu My trips', appUrl(MY_TRIPS_PATH)),
                  )}
                  {user && menuRow('/friends', 'Friends to visit', Users, pathname === '/friends')}
                  {menuRow(
                    '/travel-impact-calculator',
                    'Travel Impact Calculator',
                    Calculator,
                    pathname === '/travel-impact-calculator',
                  )}
                  {loading ? (
                    <span className={cn(greetingClass, 'h-12 gap-2.5')}>Loading…</span>
                  ) : user ? (
                    <>
                      <span className={cn(greetingClass, 'h-12 gap-2.5')}>
                        <UserRound className="h-[18px] w-[18px]" aria-hidden="true" />
                        <span className="truncate">Hello {user.firstName}</span>
                      </span>
                      <button type="button" onClick={handleLogout} className={cn(utilityLinkClass, 'h-12 gap-2.5')}>
                        <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />
                        Log out
                      </button>
                    </>
                  ) : (
                    menuRow(loginHref, 'Log in', UserRound, false, trackCta('Header Menu Login', appUrl(loginHref)))
                  )}
                </div>

                {SOCIAL_LINKS.length > 0 && (
                  <div className="mt-auto border-t border-jody-neutral-200 pt-5">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-jody-neutral-500">Follow Jody</p>
                    <div className="mt-2 flex items-center gap-1">
                      {SOCIAL_LINKS.map(({ name, url, Icon }) => (
                        <a
                          key={name}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Jody on ${name}`}
                          onClick={trackCta(`Header Menu ${name}`, url)}
                          className="-ml-2.5 mr-2.5 flex h-11 w-11 items-center justify-center rounded-full text-jody-neutral-600 transition-colors hover:bg-jody-neutral-100 hover:text-jody-forest"
                        >
                          <Icon size={22} aria-hidden="true" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
